namespace Solovis.Reports.Parameters {

  export interface TimePeriodOptions {
    basic?: TimePeriodOptionsBasic;
    timeSeries?: TimePeriodOptionsTimeSeries;
    rollingPeriods?: TimePeriodOptionsRollingPeriod;
  }

  export interface TimePeriodOptionsBasic {
    oneDay?: boolean;
    mtd?: boolean;
    qtd?: boolean;
    ytd?: boolean;
    fytd?: boolean;
    oneYear?: boolean;
    threeYears?: boolean;
    fiveYears?: boolean;
    sevenYears?: boolean;
    tenYears?: boolean;

    sinceInception?: boolean;
    sinceFirstFullMonth?: boolean;
    sinceFirstFullQuarter?: boolean;

    sinceMonthsBack?: number[];
    sinceYearsBack?: number[];
    sinceDayIds?: number[];
  }

  export interface TimePeriodOptionsTimeSeries {
    periodType?: "PeriodsBack" | "SinceDate";
    periodLength?: "Months" | "Quarters" | "Years";
    numberOfPeriods?: number;
    sinceDayId?: number;
  }

  export interface TimePeriodOptionsRollingPeriod {
    rollingIntervals?: InvData.Util.IntervalSetRollingInterval[];
    daily?: InvData.Util.IntervalSetDaily;
    sinceDateCumulativeIntervals?: InvData.Util.IntervalSetSinceDateCumulativeInterval[];
    sinceInception?: boolean;
  }

  export class TimePeriodsParameterControl extends Solovis.UI.BaseControl {
    private _intervalSetOptions;
    private _intervalSetLabel;
    private _intervalSetEditLink;
    private _sectionsInUse: string[];

    constructor(options) {
      super(options);
      this._intervalSetOptions = (typeof options.initialValue !== "undefined") ? options.initialValue : null;
      this._sectionsInUse = options.settings?.sectionsInUse ?? [];
    }

    public doRender() {
      let self = this;

      this._intervalSetLabel = $("<a>").text("Time Periods").appendTo(this.container);
      this._intervalSetLabel.click(function () {
        self._showEditor();
      });

      this._intervalSetEditLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      this._intervalSetEditLink.click(function () {
        self._showEditor();
      });
    }

    private _showEditor() {
      let editorDialog = new TimePeriodsEditorDialog({
        sections: this._sectionsInUse,
        intervalSetOptions: this._intervalSetOptions,
        saveCallback: (newIntervalSetOptions: Solovis.Reports.Parameters.TimePeriodOptions) => {
          this.setParameterValue(newIntervalSetOptions);
        }
      });

      editorDialog.show();
    }

    public _setCurrentIntervalSetOptions(newInteralSetOptions) {
      this._intervalSetOptions = newInteralSetOptions;
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, TimePeriodsParameterControl, this._setCurrentIntervalSetOptions, newValue, more);
    }

    public getParameterValue() {
      return Solovis.deepCopy(this._intervalSetOptions);
    }

    public notifyParameterValueChanged() {
      $(this).trigger("parameterValueChanged");
    }
  }


  export class TimePeriodsEditorDialog {

    private _timePeriodOptions: Solovis.Reports.Parameters.TimePeriodOptions;
    private _sections: string[];
    private _saveCallback: (options: any) => void;

    private _formBuilder: Solovis.UI.InputControls.FormBuilder;

    private _hasCustomFiscalYearStartMonth: boolean;

    public constructor(options: {
      sections: string[];
      intervalSetOptions: Solovis.Reports.Parameters.TimePeriodOptions;
      saveCallback: any;
    }) {
      this._timePeriodOptions = options.intervalSetOptions ? options.intervalSetOptions : {};
      this._sections = options.sections ? options.sections : [];
      this._saveCallback = options.saveCallback;

      let fiscalYearStartMonth = 1;
      const solovisAny = (window as any).Solovis;
      if (solovisAny?.Custom?.ModelDefaults?.fiscalYearStartMonth) {
        fiscalYearStartMonth = solovisAny.Custom.ModelDefaults.fiscalYearStartMonth;
      }

      this._hasCustomFiscalYearStartMonth = fiscalYearStartMonth !== 1;
    }

    public show() {
      const formBuilderDiv = $("<div>");
      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: formBuilderDiv,
        width: 800,
        labelColumnWidth: 245
      });
      this._formBuilder = formBuilder;

      if (this._sections.includes("basic")) {
        let basicOptions = this._timePeriodOptions.basic ? this._timePeriodOptions.basic : {};
        formBuilder.addEntry({
          name: "basicHeader",
          label: "Basic",
          isGroupHeader: true
        });

        formBuilder.addEntry({
          name: "basic",
          label: "",

          initialValue: {
            oneDay: basicOptions.oneDay,
            mtd: basicOptions.mtd,
            qtd: basicOptions.qtd,
            ytd: basicOptions.ytd,
            fytd: basicOptions.fytd,
            oneYear: basicOptions.oneYear,
            threeYears: basicOptions.threeYears,
            fiveYears: basicOptions.fiveYears,
            sevenYears: basicOptions.sevenYears,
            tenYears: basicOptions.tenYears,
          },

          createEditor: (createEditorOptions) => {
            let container = createEditorOptions.container;
            container.css({
              "padding-top": "8px",
              "padding-bottom": "10px",
              "display": "flex",
              "flex-direction": "column",
              "flex-wrap": "wrap",
              "gap": "10px"
            });

            const createFlexRow = () => {
              return $("<div>").css({
                "display": "flex",
                "flex-direction": "row",
                "gap": "10px"
              }).appendTo(container);
            };

            // First row
            let row1 = createFlexRow();
            let oneDayCheckBox = $("<div>").appendTo(row1).dxCheckBox({
              text: "1-Day", value: !!basicOptions.oneDay
            }).dxCheckBox("instance");

            let mtdCheckBox = $("<div>").appendTo(row1).dxCheckBox({
              text: "MTD", value: !!basicOptions.mtd
            }).dxCheckBox("instance");

            let qtdCheckBox = $("<div>").appendTo(row1).dxCheckBox({
              text: "QTD", value: !!basicOptions.qtd
            }).dxCheckBox("instance");

            let ytdCheckBox = $("<div>").appendTo(row1).dxCheckBox({
              text: "YTD", value: !!basicOptions.ytd
            }).dxCheckBox("instance");

            let fytdCheckBox;
            if (basicOptions.fytd || this._hasCustomFiscalYearStartMonth) {
              fytdCheckBox = $("<div>").appendTo(row1).dxCheckBox({
                text: "FYTD", value: !!basicOptions.fytd
              }).dxCheckBox("instance");
            }

            // Second row
            let row2 = createFlexRow();
            let oneYearCheckBox = $("<div>").appendTo(row2).dxCheckBox({
              text: "1Y", value: !!basicOptions.oneYear
            }).dxCheckBox("instance");

            let threeYearCheckBox = $("<div>").appendTo(row2).dxCheckBox({
              text: "3Y", value: !!basicOptions.threeYears
            }).dxCheckBox("instance");

            let fiveYearCheckBox = $("<div>").appendTo(row2).dxCheckBox({
              text: "5Y", value: !!basicOptions.fiveYears
            }).dxCheckBox("instance");

            let sevenYearCheckBox = $("<div>").appendTo(row2).dxCheckBox({
              text: "7Y", value: !!basicOptions.sevenYears
            }).dxCheckBox("instance");

            let tenYearCheckBox = $("<div>").appendTo(row2).dxCheckBox({
              text: "10Y", value: !!basicOptions.tenYears
            }).dxCheckBox("instance");

            // Third row
            let row3 = createFlexRow();
            let sinceInceptionCheckBox = $("<div>").appendTo(row3).dxCheckBox({
              text: "Since Inception", value: !!basicOptions.sinceInception
            }).dxCheckBox("instance");

            let sinceFirstFullMonthCheckBox = $("<div>").appendTo(row3).dxCheckBox({
              text: "Since First Full Month", value: !!basicOptions.sinceFirstFullMonth
            }).dxCheckBox("instance");

            let sinceFirstFullQuarterCheckBox = $("<div>").appendTo(row3).dxCheckBox({
              text: "Since First Full Quarter", value: !!basicOptions.sinceFirstFullQuarter
            }).dxCheckBox("instance");

            let editor = {
              getValue: () => {
                let result: Solovis.Reports.Parameters.TimePeriodOptionsBasic = {};
                if (oneDayCheckBox.option("value")) {
                  result.oneDay = true;
                }
                if (mtdCheckBox.option("value")) {
                  result.mtd = true;
                }
                if (qtdCheckBox.option("value")) {
                  result.qtd = true;
                }
                if (ytdCheckBox.option("value")) {
                  result.ytd = true;
                }
                if (this._hasCustomFiscalYearStartMonth) {
                  if (fytdCheckBox.option("value")) {
                    result.fytd = true;
                  }
                }
                if (oneYearCheckBox.option("value")) {
                  result.oneYear = true;
                }
                if (threeYearCheckBox.option("value")) {
                  result.threeYears = true;
                }
                if (fiveYearCheckBox.option("value")) {
                  result.fiveYears = true;
                }
                if (sevenYearCheckBox.option("value")) {
                  result.sevenYears = true;
                }
                if (tenYearCheckBox.option("value")) {
                  result.tenYears = true;
                }
                if (sinceInceptionCheckBox.option("value")) {
                  result.sinceInception = true;
                }
                if (sinceFirstFullMonthCheckBox.option("value")) {
                  result.sinceFirstFullMonth = true;
                }
                if (sinceFirstFullQuarterCheckBox.option("value")) {
                  result.sinceFirstFullQuarter = true;
                }

                return result;
              }
            };

            return editor;
          }
        });
        formBuilder.addEntry(IntervalSetEditorDialog.GetSinceMonthsBackOptions(basicOptions));
        formBuilder.addEntry(IntervalSetEditorDialog.GetSinceYearsBackOptions(basicOptions));
        formBuilder.addEntry(IntervalSetEditorDialog.GetSinceDayIdsOptions(basicOptions));
      }

      if (this._sections.includes("timeSeries")) {
        let timeSeriesOptions = this._timePeriodOptions.timeSeries ? this._timePeriodOptions.timeSeries : {};
        let timeSeriesHeaderEntry = formBuilder.addEntry({
          name: "timeSeriesHeader",
          label: "Time Series",
          isGroupHeader: true
        });
        timeSeriesHeaderEntry._entryDiv.css("margin-bottom", "10px");

        formBuilder.addEntry({
          name: "periodType",
          label: "Period Type",

          initialValue: timeSeriesOptions.periodType ? timeSeriesOptions.periodType : "PeriodsBack",

          editor: {
            type: "select",
            selectOptions: {
              data: [
                { text: "Periods Back", value: "PeriodsBack" },
                { text: "Since Date", value: "SinceDate" },
              ]
            }
          }
        });

        formBuilder.addEntry({
          name: "periodLength",
          label: "Period Length",

          initialValue: timeSeriesOptions.periodLength ? timeSeriesOptions.periodLength : "Months",

          editor: {
            type: "select",
            selectOptions: {
              data: [
                { text: "Months", value: "Months" },
                { text: "Quarters", value: "Quarters" },
                { text: "Years", value: "Years" },
              ]
            }
          }
        });

        formBuilder.addEntry({
          name: "numberOfPeriods",
          label: "Number of Periods",

          initialValue: timeSeriesOptions.numberOfPeriods ? timeSeriesOptions.numberOfPeriods : null,

          createEditor: (createEditorOptions) => {
            let editorControl = new Solovis.UI.InputControl({
              container: createEditorOptions.container,
              type: "number",
              decimals: 0,
              width: "100%",
              placeholder: "Enter number",
              value: createEditorOptions.initialValue,
              validationModel: createEditorOptions.validationModel
            });

            let editor = Solovis.UI.InputControls.FormEntryFactoryManager.WrapEditor(editorControl);

            let originalGetValue = editor.getValue;
            editor.getValue = () => {
              let strValue = originalGetValue();
              if (Solovis.isNotNull(strValue) && strValue.trim() !== "") {
                return parseInt(strValue);
              }
              return null;
            };

            return editor;
          },

          isVisible: (formEntry) => formEntry.formBuilder.getValueByName("periodType") !== "SinceDate"
        });

        formBuilder.addEntry({
          name: "sinceDayId",
          label: "Since Date",

          initialValue: timeSeriesOptions.sinceDayId ? timeSeriesOptions.sinceDayId : null,

          createEditor: (createEditorOptions) => {
            let getDateForValue = (incomingValue) => {
              if (incomingValue instanceof Date) {
                return incomingValue;
              } else if (typeof incomingValue === "number") {
                return InvData.DayId.toDate(incomingValue);
              }
              return null;
            };

            let editorControl = new Solovis.UI.InputControl({
              container: createEditorOptions.container,
              type: "date",
              width: "100%",
              value: getDateForValue(createEditorOptions.initialValue),
              validationModel: createEditorOptions.validationModel
            });

            let editor = Solovis.UI.InputControls.FormEntryFactoryManager.WrapEditor(editorControl);

            let originalGetValue = editor.getValue;
            let originalSetValue = editor.setValue;

            editor.getValue = () => {
              let dateValue = originalGetValue();
              return dateValue ? InvData.DayId.forDate(dateValue) : null;
            };

            editor.setValue = (newValue) => {
              originalSetValue(getDateForValue(newValue));
            };

            return editor;
          },

          isVisible: (formEntry) => formEntry.formBuilder.getValueByName("periodType") === "SinceDate"
        });

        formBuilder.bindValueChanged((e, data) => {
          if (data.formEntry && data.formEntry.name === "periodType") {
            if (formBuilder.getValueByName("periodType") === "SinceDate") {
              formBuilder.getEntryByName("numberOfPeriods").value(null);
            } else {
              formBuilder.getEntryByName("sinceDayId").value(null);
            }
          }
        });
      }

      if (this._sections.includes("rollingPeriods")) {
        let rollingOptions = this._timePeriodOptions.rollingPeriods ?? {};
        formBuilder.addEntry({
          name: "rollingHeader",
          label: "Rolling Periods",
          isGroupHeader: true
        });
        formBuilder.addEntry(IntervalSetEditorDialog.GetRollingIntervalsOptions(rollingOptions.rollingIntervals));
        formBuilder.addEntry(IntervalSetEditorDialog.GetDailyDaysBackOptions(rollingOptions.daily));
        formBuilder.addEntry(IntervalSetEditorDialog.GetSinceDateCumulativeIntervalsOptions(rollingOptions.sinceDateCumulativeIntervals));
        formBuilder.addEntry({
          name: "rollingPeriods.sinceInception",
          label: "Since Inception",
          initialValue: rollingOptions.sinceInception ?? false,
          editor: { type: "bool" },
        });
      }

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Time Periods",
        desiredWidth: 800,
        desiredHeight: 400,
        showSaveCancel: true,
        saveButtonText: "OK",

        saveCallback: () => {
          let newOptions: Solovis.Reports.Parameters.TimePeriodOptions = {};
          if (this._sections.includes("basic")) {
            let basicNewOptions: Solovis.Reports.Parameters.TimePeriodOptionsBasic = {};
            basicNewOptions = this._formBuilder.getValueByName("basic");

            let sinceDayIds = this._formBuilder.getValueByName("sinceDayIds");
            if (sinceDayIds) {
              basicNewOptions.sinceDayIds = sinceDayIds;
            }

            let sinceMonthsBack = this._formBuilder.getValueByName("sinceMonthsBack");
            if (sinceMonthsBack) {
              basicNewOptions.sinceMonthsBack = sinceMonthsBack;
            }

            let sinceYearsBack = this._formBuilder.getValueByName("sinceYearsBack");
            if (sinceYearsBack) {
              basicNewOptions.sinceYearsBack = sinceYearsBack;
            }

            newOptions.basic = basicNewOptions;
          }
          if (this._sections.includes("timeSeries")) {
            let periodType = this._formBuilder.getValueByName("periodType");
            let timeSeriesNewOptions: Solovis.Reports.Parameters.TimePeriodOptionsTimeSeries = {
              periodType: periodType,
              periodLength: this._formBuilder.getValueByName("periodLength"),
            };

            if (periodType === "SinceDate") {
              timeSeriesNewOptions.sinceDayId = this._formBuilder.getValueByName("sinceDayId");
            } else {
              timeSeriesNewOptions.numberOfPeriods = this._formBuilder.getValueByName("numberOfPeriods");
            }

            newOptions.timeSeries = timeSeriesNewOptions;
          }
          if (this._sections.includes("rollingPeriods")) {
            let rollingNewOptions: Solovis.Reports.Parameters.TimePeriodOptionsRollingPeriod = {};
            rollingNewOptions.rollingIntervals = this._formBuilder.getValueByName("rollingIntervals");
            let dailyDaysBack = this._formBuilder.getValueByName("dailyDaysBack");
            if (dailyDaysBack) {
              rollingNewOptions.daily = { daysBack: dailyDaysBack };
            }
            rollingNewOptions.sinceDateCumulativeIntervals = this._formBuilder.getValueByName("sinceDateCumulativeIntervals");
            rollingNewOptions.sinceInception = this._formBuilder.getValueByName("rollingPeriods.sinceInception");
            newOptions.rollingPeriods = rollingNewOptions;
          }

          this._saveCallback(newOptions);
          return true;
        }
      });
    }
  }
}